import { useSyncExternalStore } from "react";
import { TradeSyncStaleReason, TradeSyncStatus } from "./tradeSync.types";

/**
 * Per-target sync status, so a view can say "this list could not be confirmed"
 * instead of presenting an unconfirmed answer as current.
 *
 * Same module-`Set` store shape as `pendingCloses`, consumed with
 * `useSyncExternalStore`.
 */

const LIVE: TradeSyncStatus = { state: "LIVE" };

const listeners = new Set<() => void>();
let statuses: Readonly<Record<string, TradeSyncStatus>> = {};

const commit = (next: Record<string, TradeSyncStatus>) => {
  statuses = next;
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // One bad subscriber must not stop the others.
    }
  });
};

export const subscribeSyncStatus = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Identity-stable between commits, as `useSyncExternalStore` requires. */
export const getSyncStatuses = (): Readonly<Record<string, TradeSyncStatus>> =>
  statuses;

export const getSyncStatus = (targetId: string): TradeSyncStatus =>
  statuses[targetId] || LIVE;

export const setSyncing = (targetId: string, attempt: number, maxAttempts: number) => {
  // Build a fresh status rather than spreading the previous one. Spreading
  // carried a STALE `reason` into SYNCING, so a target recovering from a network
  // failure reported "syncing" while still claiming reason NETWORK — and any UI
  // that renders `reason` when present showed a stale error mid-sync.
  // `lastConvergedAt` is the one field worth keeping: it records when this target
  // last PROVED it was current, which a new attempt does not invalidate.
  commit({
    ...statuses,
    [targetId]: {
      state: "SYNCING",
      lastConvergedAt: getSyncStatus(targetId).lastConvergedAt,
      attempt,
      maxAttempts,
    },
  });
};

export const setLive = (targetId: string, now = Date.now()) => {
  commit({ ...statuses, [targetId]: { state: "LIVE", lastConvergedAt: now } });
};

export const setStale = (targetId: string, reason: TradeSyncStaleReason) => {
  commit({
    ...statuses,
    [targetId]: {
      state: "STALE",
      reason,
      lastConvergedAt: getSyncStatus(targetId).lastConvergedAt,
    },
  });
};

export const clearSyncStatus = (targetId: string) => {
  if (!statuses[targetId]) return;
  const next = { ...statuses };
  delete next[targetId];
  commit(next);
};

/** Test seam. */
export const resetSyncStatuses = () => commit({});

/**
 * Read one target's status in a component.
 *
 * The store is a module singleton read through `useSyncExternalStore`, so this
 * keeps the subscribe/getSnapshot pairing in one place instead of each consumer
 * re-deriving it — the paginated-history hook grew its own copy, and a second
 * copy is how the two drift.
 */
export const useSyncStatus = (targetId: string): TradeSyncStatus => {
  const statuses = useSyncExternalStore(
    subscribeSyncStatus,
    getSyncStatuses,
    getSyncStatuses
  );
  return statuses[targetId] || LIVE;
};
